import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Skeleton } from '../ui/skeleton';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, MousePointerClick, Save, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../config';
import PhotoPickerDialog from './PhotoPickerDialog';

const ALL_PLACEMENTS = [
  { key: 'routes',   label: 'Routes page' },
  { key: 'airlines', label: 'Airlines page' },
  { key: 'blog',     label: 'Blog page' },
  { key: 'discover', label: 'Discover page' },
  { key: 'airport',  label: 'Airport pages' },
];

const EMPTY_FORM = {
  title: '', description: '', image_url: '',
  cta_text: 'Learn More', cta_url: '/contact',
  placements: [], position: 4, badge: 'Sponsored', active: true,
};

export default function AdsManager() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);

  const fetchAds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ads`, { withCredentials: true });
      setAds(res.data.items || []);
    } catch { toast.error('Failed to load ads'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  const openCreate = () => {
    setEditingAd(null);
    setForm(EMPTY_FORM);
    setEditOpen(true);
  };

  const openEdit = (ad) => {
    setEditingAd(ad);
    setForm({
      title: ad.title || '',
      description: ad.description || '',
      image_url: ad.image_url || '',
      cta_text: ad.cta_text || 'Learn More',
      cta_url: ad.cta_url || '/contact',
      placements: ad.placements || [],
      position: ad.position || 4,
      badge: ad.badge || 'Sponsored',
      active: !!ad.active,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.placements.length) { toast.error('Select at least one placement'); return; }
    setSaving(true);
    try {
      if (editingAd) {
        const res = await axios.put(`${API}/admin/ads/${editingAd.ad_id}`, form, { withCredentials: true });
        setAds(prev => prev.map(a => a.ad_id === editingAd.ad_id ? res.data : a));
        toast.success('Ad updated!');
      } else {
        const res = await axios.post(`${API}/admin/ads`, form, { withCredentials: true });
        setAds(prev => [res.data, ...prev]);
        toast.success('Ad created!');
      }
      setEditOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save ad'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (ad) => {
    try {
      const res = await axios.patch(`${API}/admin/ads/${ad.ad_id}/toggle`, {}, { withCredentials: true });
      setAds(prev => prev.map(a => a.ad_id === ad.ad_id ? { ...a, active: res.data.active } : a));
      toast.success(res.data.active ? 'Ad activated' : 'Ad paused');
    } catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (adId) => {
    try {
      await axios.delete(`${API}/admin/ads/${adId}`, { withCredentials: true });
      setAds(prev => prev.filter(a => a.ad_id !== adId));
      toast.success('Ad deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const togglePlacement = (key) => {
    setForm(p => ({
      ...p,
      placements: p.placements.includes(key)
        ? p.placements.filter(k => k !== key)
        : [...p.placements, key],
    }));
  };

  if (loading) return <Skeleton className="h-40 rounded-2xl" />;

  return (
    <>
      <Card className="fly-card overflow-hidden">
        {/* Header */}
        <div className="p-3 border-b border-border flex items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold">Ad Campaigns</p>
            <p className="text-xs text-muted-foreground">{ads.filter(a => a.active).length} active · {ads.length} total</p>
          </div>
          <Button size="sm" className="h-8 text-xs fly-btn-primary" onClick={openCreate} data-testid="create-ad-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Create Ad
          </Button>
        </div>

        {ads.length === 0 ? (
          <div className="p-12 text-center">
            <MousePointerClick className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium mb-1">No ads yet</p>
            <p className="text-xs text-muted-foreground mb-4">Create your first ad to start monetizing your traffic</p>
            <Button size="sm" className="fly-btn-primary" onClick={openCreate}>Create First Ad</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-[200px]">Placements</TableHead>
                  <TableHead className="w-[70px]">Position</TableHead>
                  <TableHead className="w-[70px]">Clicks</TableHead>
                  <TableHead className="w-[80px]">Status</TableHead>
                  <TableHead className="w-[120px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ads.map(ad => (
                  <TableRow key={ad.ad_id} className={!ad.active ? 'opacity-50' : ''} data-testid={`ad-row-${ad.ad_id}`}>
                    <TableCell>
                      {ad.image_url ? (
                        <img src={ad.image_url} alt="" className="w-8 h-8 rounded object-cover" loading="lazy" onError={e => e.target.style.display='none'} />
                      ) : (
                        <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                          <ImageIcon className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium line-clamp-1">{ad.title}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[220px]">{ad.cta_url}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(ad.placements || []).map(p => (
                          <Badge key={p} className="text-[9px] px-1.5 capitalize">{p}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">After #{ad.position}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MousePointerClick className="w-3 h-3" />{ad.clicks || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`text-[10px] cursor-pointer ${ad.active ? 'bg-green-500/15 text-green-600' : 'bg-zinc-500/15 text-zinc-500'}`}
                        onClick={() => handleToggle(ad)}
                        title="Click to toggle"
                      >
                        {ad.active ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[#FF4500]" onClick={() => openEdit(ad)} data-testid={`ad-edit-${ad.ad_id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2"
                          onClick={() => handleToggle(ad)} title={ad.active ? 'Pause' : 'Activate'}>
                          {ad.active ? <ToggleRight className="w-3.5 h-3.5 text-amber-500" /> : <ToggleLeft className="w-3.5 h-3.5 text-muted-foreground" />}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this ad?</AlertDialogTitle>
                              <AlertDialogDescription>"{ad.title}" will be permanently removed.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(ad.ad_id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* ── Create / Edit Dialog ─────────────────────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ad-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MousePointerClick className="w-5 h-5 text-[#FF4500]" />
              {editingAd ? 'Edit Ad' : 'Create New Ad'}
            </DialogTitle>
            <DialogDescription>Configure your ad content and where it appears</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            {/* Title */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">HEADLINE *</label>
              <Input placeholder="e.g. Book your next adventure" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="ad-form-title" />
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">DESCRIPTION</label>
              <Textarea rows={2} placeholder="Short compelling copy..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>

            {/* Image */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">IMAGE URL</label>
              <div className="flex gap-2">
                <Input placeholder="https://..." value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} data-testid="ad-form-image" />
                <Button size="sm" variant="outline" className="shrink-0 h-9 text-xs gap-1.5" onClick={() => setPhotoOpen(true)}>
                  <ImageIcon className="w-3.5 h-3.5" /> Browse
                </Button>
                {form.image_url && <img src={form.image_url} alt="" className="w-10 h-9 rounded object-cover shrink-0" onError={e => e.target.style.display='none'} />}
              </div>
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">BUTTON TEXT</label>
                <Input placeholder="Learn More" value={form.cta_text} onChange={e => setForm(p => ({ ...p, cta_text: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">CLICK URL</label>
                <Input placeholder="/contact" value={form.cta_url} onChange={e => setForm(p => ({ ...p, cta_url: e.target.value }))} />
              </div>
            </div>

            {/* Badge + Position */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">BADGE TEXT</label>
                <Input placeholder="Sponsored" value={form.badge} onChange={e => setForm(p => ({ ...p, badge: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">INSERT AFTER ITEM #</label>
                <Input type="number" min={1} max={20} value={form.position} onChange={e => setForm(p => ({ ...p, position: parseInt(e.target.value) || 4 }))} />
              </div>
            </div>

            {/* Placements */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-2">PLACEMENTS * (where this ad appears)</label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_PLACEMENTS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm p-2 rounded-lg border border-border hover:border-[#FF4500]/30 transition-colors">
                    <button
                      type="button"
                      onClick={() => togglePlacement(key)}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${form.placements.includes(key) ? 'bg-[#FF4500] border-[#FF4500]' : 'border-border'}`}
                    >
                      {form.placements.includes(key) && <span className="text-white text-[10px] font-bold">✓</span>}
                    </button>
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border">
              <div>
                <p className="text-sm font-medium">Ad Status</p>
                <p className="text-xs text-muted-foreground">{form.active ? 'Live — visible to users' : 'Paused — not shown'}</p>
              </div>
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, active: !p.active }))}
                className={`relative w-10 h-6 rounded-full transition-colors ${form.active ? 'bg-[#10B981]' : 'bg-muted'}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${form.active ? 'left-5' : 'left-1'}`} />
              </button>
            </div>
          </div>

          <DialogFooter className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setEditOpen(false)}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button size="sm" className="text-xs fly-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-ad-btn">
              <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : editingAd ? 'Update Ad' : 'Create Ad'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Picker */}
      <PhotoPickerDialog
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        onSelect={url => setForm(p => ({ ...p, image_url: url }))}
        initialQuery="travel advertisement"
      />
    </>
  );
}
